import React, { useEffect } from 'react';
import { DateInput, DateValue, parseISODate } from '@cad-arc';
import { CommonControlProps, IntegratedFormControl } from './integrated-form-control';
import { FieldPath, FieldValues, useWatch } from 'react-hook-form';
import { useIntegratedForm } from '../use-integrated-form';
import {
    formatMonthDayYear,
    formatMonthDayYearHoursMinutes,
    formatMonthDayYearHoursMinutesSeconds,
    getIsoDateInUserTimezone,
} from '../../common/helpers/utils';

type DateInputProps = React.ComponentProps<typeof DateInput>;

type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = CommonControlProps<TFieldValues, TName> & {
    dateInputProps?: Partial<DateInputProps>;
    granularity?: DateInputProps['granularity'];
    hasCalendar?: DateInputProps['hasCalendar'];
    minValue?: DateInputProps['minValue'];
    hideTimeZone?: DateInputProps['hideTimeZone'];
    portalElement?: HTMLDivElement | null;
};

function toSerializedDateValue(dateValue: DateValue | null): string | null {
    if (!dateValue) {
        return null;
    }
    return dateValue.toDate(Intl.DateTimeFormat().resolvedOptions().timeZone).toISOString();
}

export function toDateValueObject(
    dateValue: string | null,
    granularity?: string
): DateValue | null {
    if (!dateValue) {
        return null;
    }

    const isDateTime = granularity === 'second' || granularity === 'minute';
    const dateValueInUserTimezone = getIsoDateInUserTimezone(dateValue);

    try {
        if (isDateTime) {
            return parseISODate(dateValueInUserTimezone, 'datetime');
        } else {
            return parseISODate(dateValueInUserTimezone, 'date');
        }
    } catch {
        return null;
    }
}

export function FormDateInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: Props<TFieldValues, TName>): React.ReactElement {
    const {
        dateInputProps,
        granularity,
        hasCalendar,
        minValue,
        hideTimeZone,
        portalElement,
        ...integratedFormControlProps
    } = props;
    const integratedForm = useIntegratedForm();
    const rawDate = useWatch({ control: integratedForm.form.control, name: props.name });
    const [dateValue, setDateValue] = React.useState<DateValue | null>(
        toDateValueObject(rawDate, granularity)
    );
    const dateInputPropsMemo: Partial<DateInputProps> = React.useMemo(() => dateInputProps ?? {}, [
        dateInputProps,
    ]);

    const renderViewMode = React.useCallback(
        (value: string) => {
            switch (granularity) {
                case 'minute':
                    return formatMonthDayYearHoursMinutes(value);
                case 'second':
                    return formatMonthDayYearHoursMinutesSeconds(value);
                default:
                    return formatMonthDayYear(value);
            }
        },
        [granularity]
    );

    useEffect(
        function updateDisplayForProgrammaticUpdate() {
            const subscription = integratedForm.form.watch((value, { name }) => {
                if (name === props.name) {
                    const dateValue = toDateValueObject(value[name], props.granularity);
                    // If we're directly setting it to null, let it go through.
                    // If the parsed date is null, it might mean the user is just typing in a date, so we shouldn't change the value.
                    if (value[name] === null || dateValue !== null) {
                        setDateValue(dateValue);
                    }
                }
            });

            return () => subscription.unsubscribe();
        },
        [integratedForm.form, props.granularity, props.name]
    );

    return (
        <IntegratedFormControl<string>
            {...integratedFormControlProps}
            renderViewMode={renderViewMode}
        >
            {(providedProps) => (
                // @ts-expect-error erroneous typeerror around the type of focus handler
                <DateInput
                    {...providedProps}
                    {...dateInputPropsMemo}
                    granularity={granularity}
                    minValue={minValue}
                    hasCalendar={hasCalendar}
                    hideTimeZone={hideTimeZone}
                    value={dateValue}
                    popoverContentProps={
                        portalElement
                            ? {
                                  portalProps: {
                                      container: portalElement,
                                  },
                              }
                            : undefined
                    }
                    onChange={(dateValue) => {
                        setDateValue(dateValue);
                        providedProps.onChange(toSerializedDateValue(dateValue));
                    }}
                />
            )}
        </IntegratedFormControl>
    );
}
